import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { jsPDF } from 'jspdf';
import BrandName from './BrandName';

const ImgToPdf = () => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB per file

  // Handle file selection and validation
  const onDrop = useCallback((acceptedFiles) => {
    const validFiles = acceptedFiles.filter(file => file.size <= MAX_FILE_SIZE);
    const largeFiles = acceptedFiles.filter(file => file.size > MAX_FILE_SIZE);

    if (largeFiles.length > 0) {
      setErrorMessage(`Some files are too large. Max file size is ${MAX_FILE_SIZE / (1024 * 1024)} MB.`);
    } else {
      setErrorMessage('');
      setSelectedFiles(prev => [...prev, ...validFiles]);
    }
  }, [MAX_FILE_SIZE]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': [],
      'image/jpg': [],
      'image/png': [],
      'image/gif': [],
      'image/bmp': [],
      'image/webp': []
    },
    multiple: true,
  });

  // Convert selected images to PDF
  const handleConvert = async () => {
    if (selectedFiles.length === 0) {
      setErrorMessage('Please select image(s) to convert.');
      return;
    }

    setIsProcessing(true);
    const pdf = new jsPDF('p', 'mm', 'a4');

    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];
      const imgData = await readFileAsDataURL(file);

      const img = new Image();
      img.src = imgData;

      await new Promise((resolve) => {
        img.onload = () => {
          const imgWidth = pdf.internal.pageSize.getWidth();
          const imgHeight = (img.height * imgWidth) / img.width;

          if (i > 0) pdf.addPage();
          pdf.addImage(imgData, 'JPEG', 0, 0, imgWidth, imgHeight);
          resolve();
        };
      });
    }

    pdf.save('converted.pdf');
    setIsProcessing(false);
  };

  // Convert file to Data URL
  const readFileAsDataURL = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  return (
    <div className="container py-5">
      <div className="text-center">
        <h1 className="d-inline-flex">Image to PDF Converter by <BrandName name="convert" /></h1>
        <h4>A free tool to convert <span>JPEG, PNG, GIF, BMP, WebP</span> files to PDF.</h4>
      </div>

      <div className="selectbox">
        <div {...getRootProps()} className={`dropzone ${isDragActive ? 'active' : ''}`} style={{ border: '2px dashed gray', padding: '20px', textAlign: 'center' }}>
          <input {...getInputProps()} />
          <p>{isDragActive ? 'Drop the files here...' : 'Drag and drop images here, or click to select'}</p>
        </div>
        <p>Up to 10 images, max 10 MB each</p>
      </div>

      {selectedFiles.length > 0 && (
        <div className="selected-files mt-3">
          <h3>Selected Images:</h3>
          <ul>
            {selectedFiles.map((file, index) => (
              <li key={index}>{file.name}</li>
            ))}
          </ul>
        </div>
      )}

      {errorMessage && <p style={{ color: 'red' }}>{errorMessage}</p>}

      <button onClick={handleConvert} className="btn btn-warning mt-3" disabled={isProcessing}>
        {isProcessing ? 'Processing...' : 'Convert to PDF'}
      </button>
    </div>
  );
};

export default ImgToPdf;